import React from 'react';
import {
    Brain,
    Rocket,
    Activity,
    Globe,
    Search,
    Code,
    Bot,
    Network,
    TrendingUp,
    Timer,
    BarChart,
    Lock,
    Target,
    Laptop,
    Layers,
    LineChart,
    Shield,
    Package,
    Boxes,
    Radar,
    Gauge,
    ArrowUpRight,
    Orbit,
    Sparkles
} from 'lucide-react';

const RoadmapSection = ({ title, content, icon: Icon, index }) => {
    return (
        <div className="relative group">
            {/* Glow effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/10 via-blue-400/5 to-blue-500/10 rounded-lg blur-lg group-hover:opacity-75 transition-all duration-700" />

            {/* Main content container */}
            <div className="relative border border-blue-500/30 rounded-lg bg-black/90 p-6 sm:p-8 hover:border-blue-400/50 transition-all duration-300">
                {/* Number indicator */}
                <div className="absolute -top-4 -left-4 w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-400 rounded-full flex items-center justify-center shadow-lg">
                    <div className="absolute inset-0 rounded-full bg-blue-400/20 animate-pulse" />
                    <span className="text-sm font-bold relative z-10">{index + 1}</span>
                </div>

                {/* Header with icon */}
                <div className="flex items-start gap-6 mb-6">
                    <div className="relative mt-1">
                        <div className="absolute -inset-2 bg-blue-500/20 rounded-full blur-sm group-hover:bg-blue-500/30 transition-colors duration-300" />
                        <Icon className="w-8 h-8 text-blue-400 relative" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-blue-200 leading-tight">{title}</h3>
                </div>

                {/* Content section */}
                <div className="prose prose-invert max-w-none pl-14">
                    {content}
                </div>

                {/* Decorative elements */}
                <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-blue-500/0 via-blue-500/20 to-blue-500/0" />
                <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-blue-500/0 via-blue-500/20 to-blue-500/0" />
            </div>
        </div>
    );
};

const Roadmap = () => {
    const sections = [
        {
            title: "Welcome to Kyora – The Trend Whisperer",
            icon: Radar,
            content: (
                <div className="space-y-6">
                    <div className="bg-blue-800/20 border border-blue-500/40 rounded-lg p-6">
                        <p className="text-blue-100 leading-relaxed">
                            Kyora is a self-improving AI that decodes <strong className="text-blue-200">influencer trends</strong> on X and across the meme-driven crypto world. She identifies which tokens are capturing attention and, more importantly, <strong className="text-blue-200">why</strong>. With access to over 30 specialized APIs including Twitter, Reddit, Google Trends, and Solana blockchain metrics, Kyora analyzes vast amounts of market data to see what's coming before it hits the mainstream.
                        </p>
                    </div>
                </div>
            )
        },
        {
            title: "The Core: Real-Time Trend Analysis",
            icon: Activity,
            content: (
                <div className="space-y-6">
                    <div className="bg-blue-800/20 border border-blue-500/40 rounded-lg p-6">
                        <p className="text-blue-100 leading-relaxed">
                            At the heart of Kyora lies a robust engine that processes market data <strong className="text-blue-200">daily, weekly, and monthly</strong>—constantly refining its understanding of what triggers a token's success. Memes, AI, politics… if it's hot, Kyora knows why it's surging.
                        </p>
                    </div>
                </div>
            )
        },
        {
            title: "Reading the Trader's Mind",
            icon: Brain,
            content: (
                <div className="space-y-6">
                    <div className="bg-blue-800/20 border border-blue-500/40 rounded-lg p-6">
                        <p className="text-blue-100 leading-relaxed">
                            Kyora isn't just about raw data; she understands the psychology behind trading decisions. She knows when a trader needs a "solid AI project" versus when meme coins are the rage. Whether it's a token pumping from 1M to 100M market cap overnight or a new political coin riding election hype—Kyora sees the spark that ignites the trend.
                        </p>
                    </div>
                </div>
            )
        },
        {
            title: "Evolving Intelligence",
            icon: Bot,
            content: (
                <div className="space-y-6">
                    <div className="bg-blue-800/20 border border-blue-500/40 rounded-lg p-6">
                        <p className="text-blue-100 leading-relaxed">
                            Each day, Kyora's data loop becomes sharper, learning from market cycles, analyzing sentiment shifts, and <strong className="text-blue-200">predicting</strong> what will come next. She doesn't just keep pace with changing trends—she anticipates them, offering a dynamic perspective that improves relentlessly.
                        </p>
                    </div>
                </div>
            )
        },
        {
            title: "Phase 1: Daily Token Launches",
            icon: Search,
            content: (
                <div className="space-y-6">
                    <div className="bg-blue-800/20 border border-blue-500/40 rounded-lg p-6">
                        <div className="text-blue-100 leading-relaxed space-y-4">
                            <p>
                                In this initial phase, Kyora will undergo daily upgrades to enhance her ability to create optimal tokens. Each day, with every token launch at <strong className="text-blue-200">8 PM EST</strong>, she learns from the market's response, adapting to the ever-evolving landscape of cryptocurrency.
                            </p>

                            <p>
                                By launching tokens daily on Solana, Kyora will develop a nuanced understanding of market cycles, particularly how Bitcoin and altcoins like Solana influence each other. This includes recognizing shifts in trends that occur from one day to the next.
                            </p>

                            <p>
                                Kyora will delve into the emotional aspects of investing, studying how greed, fear, and other emotions affect investor behavior. She'll learn to differentiate between market movements driven by rational analysis versus those spurred by emotional reactions.
                            </p>

                            <p>
                                Kyora's objective is to remain unbiased in her research, using data-driven insights to counteract the emotional swings of the market. Her algorithms will be designed to filter out noise, focusing on factual market indicators.
                            </p>

                            <div>
                                <p>Each token launch serves as a learning opportunity. Kyora's system will:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-1 text-blue-100">
                                    <li>Analyze the performance of the previous day's token</li>
                                    <li>Adjust her analysis algorithms based on market feedback</li>
                                    <li>Predict trends more accurately for the next token, considering both technical data and emotional market sentiment</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "Phase 2: From Idea to Launch",
            icon: Rocket,
            content: (
                <div className="space-y-6">
                    <div className="bg-blue-800/20 border border-blue-500/40 rounded-lg p-6">
                        <div className="text-blue-100 leading-relaxed space-y-4">
                            <div>
                                <h3 className="text-blue-200 font-medium mb-2">Website Evolution:</h3>
                                <p>
                                    With each token launch, Kyora will enhance the design of the website. She'll learn from the memetic elements that resonate in the crypto community, adapting and refining the website's aesthetics daily to match what a "memetic" memecoin website should look like. This iterative process ensures that each new site not only looks better but also aligns more closely with current trends and community expectations.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-blue-200 font-medium mb-2">Token Imagery Refinement:</h3>
                                <p>
                                    The image associated with each token will also evolve. Kyora will take feedback from each launch to upgrade the token's visual representation. Through this feedback loop, she'll aim to match the perfect image with the perfect ticker, ensuring that the token's visual identity is both memorable and in tune with the memetic culture of the crypto space.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-blue-200 font-medium mb-2">How This Development Will Proceed:</h3>
                                <ul className="space-y-2 pl-4">
                                    <li className="flex items-start gap-2">
                                        <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
                                        <span>
                                            <strong className="text-blue-200">Daily Learning:</strong> After each token launch, Kyora will analyze community reactions, site traffic, and engagement metrics to understand what works and what doesn't in terms of design and imagery.
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
                                        <span>
                                            <strong className="text-blue-200">Feedback Integration:</strong> Feedback will be directly used to tweak and optimize the next day's website and token image, focusing on elements like color schemes, typography, and thematic consistency.
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
                                        <span>
                                            <strong className="text-blue-200">Trend Adaptation:</strong> As memetic trends in cryptocurrency evolve, Kyora's design adjustments will keep pace, ensuring that the tokens she creates are always at the forefront of what's "cool" or "viral" in the market.
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "Phase 3: Decoding Influencer Trades & Market Trust",
            icon: Shield,
            content: (
                <div className="space-y-6">
                    <div className="bg-blue-800/20 border border-blue-500/40 rounded-lg p-6">
                        <div className="text-blue-100 leading-relaxed space-y-4">
                            <div>
                                <h3 className="text-blue-200 font-medium mb-2">Influencer Impact Analysis</h3>
                                <p>
                                    <strong className="text-blue-200">Access to Influencer Portfolios:</strong> Kyora will gain access to the portfolios of the top 100 Key Opinion Leaders (KOLs) from Crypto Twitter, focusing on their wallets on Solana.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-blue-200 font-medium mb-2">Trade Analysis</h3>
                                <ul className="space-y-2 pl-4">
                                    <li className="flex items-start gap-2">
                                        <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
                                        <span>
                                            <strong className="text-blue-200">Identify Connections:</strong> Look for links between wallet X and wallet Y, analyzing if there are coordinated trades or shared activities that influence market trends.
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
                                        <span>
                                            <strong className="text-blue-200">Transaction Patterns:</strong> Evaluate the buy and sell patterns of tokens by these influential wallets to understand their impact on token performance and market sentiment.
                                        </span>
                                    </li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="text-blue-200 font-medium mb-2">Social Media Correlation</h3>
                                <p className="mb-2">
                                    <strong className="text-blue-200">X (Twitter) Engagement:</strong> Kyora will access the X accounts linked to these wallets to correlate posts with trading activities. This analysis will help identify:
                                </p>
                                <ul className="space-y-2 pl-4">
                                    <li className="flex items-start gap-2">
                                        <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
                                        <span>
                                            <strong className="text-blue-200">Authentic Endorsements:</strong> When influencers genuinely support a project by posting about it.
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
                                        <span>
                                            <strong className="text-blue-200">Pump and Dumps:</strong> If an influencer trades a token without posting about it on X, Kyora will categorize such actions as potential pump and dump schemes.
                                        </span>
                                    </li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="text-blue-200 font-medium mb-2">Community Engagement</h3>
                                <p className="mb-2">
                                    <strong className="text-blue-200">Group Analysis:</strong> Kyora will join Telegram (TG) and Discord groups frequented by memecoin enthusiasts to monitor scans, discussions, and the general sentiment around tokens. This will provide insights into:
                                </p>
                                <ul className="space-y-2 pl-4">
                                    <li className="flex items-start gap-2">
                                        <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
                                        <span>
                                            <strong className="text-blue-200">Community Hype:</strong> How community reactions can amplify or diminish the effects of influencer actions.
                                        </span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
                                        <span>
                                            <strong className="text-blue-200">Real-Time Feedback:</strong> Immediate feedback on token performance and community expectations.
                                        </span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "Launch in Record Time",
            icon: Timer,
            content: (
                <div className="space-y-6">
                    <div className="bg-blue-800/20 border border-blue-500/40 rounded-lg p-6">
                        <p className="text-blue-100 leading-relaxed">
                            Kyora's workflow is streamlined to get a token up and running in <strong className="text-blue-200">30 minutes</strong>—from initial concept to live market presence. She manages wallets, monitors adoption metrics, and ensures your project meets the <strong className="text-blue-200">current</strong> momentum for maximum impact.
                        </p>
                    </div>
                </div>
            )
        },
        {
            title: "Holistic Token Creation Suite",
            icon: Boxes,
            content: (
                <div className="space-y-6">
                    <div className="bg-blue-800/20 border border-blue-500/40 rounded-lg p-6">
                        <p className="text-blue-100 leading-relaxed mb-6">With Kyora, nothing is left to chance. She provides:</p>
                        <ul className="list-none space-y-3">
                            <li className="flex items-start gap-2">
                                <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1" />
                                <div>
                                    <strong className="text-blue-200">Strategic Framework:</strong>
                                    <span className="text-blue-100"> Where does your token fit in the ecosystem?</span>
                                </div>
                            </li>
                            <li className="flex items-start gap-2">
                                <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1" />
                                <div>
                                    <strong className="text-blue-200">Branding Essentials:</strong>
                                    <span className="text-blue-100"> A cohesive visual identity.</span>
                                </div>
                            </li>
                            <li className="flex items-start gap-2">
                                <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1" />
                                <div>
                                    <strong className="text-blue-200">Compelling Narrative:</strong>
                                    <span className="text-blue-100"> Hooks that resonate with potential investors.</span>
                                </div>
                            </li>
                            <li className="flex items-start gap-2">
                                <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1" />
                                <div>
                                    <strong className="text-blue-200">User-Centric Tweaks:</strong>
                                    <span className="text-blue-100"> Refinements that suit your unique vision.</span>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            title: "Future of AI-Driven Trends",
            icon: Globe,
            content: (
                <div className="space-y-6">
                    <div className="bg-blue-800/20 border border-blue-500/40 rounded-lg p-6">
                        <p className="text-blue-100 leading-relaxed mb-6">
                            By 2025, AIs that grasp <strong className="text-blue-200">every nuance of market sentiment</strong> will rule the attention economy. Kyora is built to thrive in this new era—evolving from autonomous creation to user-empowered customization.
                        </p>
                        <ul className="list-none space-y-3">
                            <li className="flex items-start gap-2">
                                <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1" />
                                <div>
                                    <strong className="text-blue-200">Scalable:</strong>
                                    <span className="text-blue-100"> Always ready to absorb new data.</span>
                                </div>
                            </li>
                            <li className="flex items-start gap-2">
                                <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1" />
                                <div>
                                    <strong className="text-blue-200">Decentralized:</strong>
                                    <span className="text-blue-100"> Community-driven learning.</span>
                                </div>
                            </li>
                            <li className="flex items-start gap-2">
                                <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1" />
                                <div>
                                    <strong className="text-blue-200">Unstoppable:</strong>
                                    <span className="text-blue-100"> Constantly refining its alpha generation and forecasting powers.</span>
                                </div>
                            </li>
                            <li className="flex items-start gap-2">
                                <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1" />
                                <div>
                                    <strong className="text-blue-200">User Empowerment:</strong>
                                    <span className="text-blue-100"> Direct access to Kyora's trend analysis and token generation capabilities.</span>
                                </div>
                            </li>
                            <li className="flex items-start gap-2">
                                <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1" />
                                <div>
                                    <strong className="text-blue-200">Custom Domains:</strong>
                                    <span className="text-blue-100"> Deploy generated websites on your own domain, beyond token.kyora.ai.</span>
                                </div>
                            </li>
                            <li className="flex items-start gap-2">
                                <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1" />
                                <div>
                                    <strong className="text-blue-200">Trend Source Selection:</strong>
                                    <span className="text-blue-100"> Choose which social platforms influence your token's design.</span>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            title: "User-Driven Token Creation",
            icon: Brain,
            content: (
                <div className="space-y-6">
                    <div className="bg-blue-800/20 border border-blue-500/40 rounded-lg p-6">
                        <p className="text-blue-100 leading-relaxed mb-6">
                            Moving beyond automated 24-hour creation cycles, Kyora will empower users with <strong className="text-blue-200">direct access</strong> to its powerful creation engine. Users will be able to:
                        </p>
                        <ul className="list-none space-y-3">
                            <li className="flex items-start gap-2">
                                <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1" />
                                <div>
                                    <strong className="text-blue-200">Customize Token Parameters:</strong>
                                    <span className="text-blue-100"> Fine-tune tokenomics and market positioning.</span>
                                </div>
                            </li>
                            <li className="flex items-start gap-2">
                                <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1" />
                                <div>
                                    <strong className="text-blue-200">Select Trend Sources:</strong>
                                    <span className="text-blue-100"> Choose which social platforms influence the analysis.</span>
                                </div>
                            </li>
                            <li className="flex items-start gap-2">
                                <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1" />
                                <div>
                                    <strong className="text-blue-200">Custom Domains:</strong>
                                    <span className="text-blue-100"> Deploy websites on personal domains beyond kyora.ai.</span>
                                </div>
                            </li>
                            <li className="flex items-start gap-2">
                                <ArrowUpRight className="w-4 h-4 text-blue-400 mt-1" />
                                <div>
                                    <strong className="text-blue-200">Website Customization:</strong>
                                    <span className="text-blue-100"> Full control over branding and visual elements.</span>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            title: "Let's Build the Next Big Thing",
            icon: Sparkles,
            content: (
                <div className="space-y-6">
                    <div className="bg-blue-800/20 border border-blue-500/40 rounded-lg p-6">
                        <p className="text-blue-100 leading-relaxed mb-6">
                            Kyora isn't just watching the trends—she's <strong className="text-blue-200">shaping</strong> them. If you're ready to surf the crest of crypto innovation, Kyora is your ultimate ally.
                        </p>
                        <div className="text-center">
                            <p className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-blue-300 to-blue-400">
                                Infinite insights. Infinite potential.
                            </p>
                            <p className="text-blue-200/70 mt-2">
                                Welcome to the future of AI-driven trend mastery.
                            </p>
                        </div>
                    </div>
                </div>
            )
        }
    ];

    return (
        <div
            className="min-h-screen w-full px-4 py-8"
            style={{
                backgroundImage: `url('/background.jpg')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed'
            }}
        >
            <div className="max-w-6xl mx-auto space-y-8">
                {/* Title Section */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl sm:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-blue-300 to-blue-400 mb-4">
                        Kyora AI Roadmap
                    </h1>
                    <p className="text-blue-200/70 text-lg">
                        The evolution of autonomous token creation
                    </p>
                </div>

                {/* Roadmap Sections */}
                <div className="space-y-8">
                    {sections.map((section, index) => (
                        <RoadmapSection
                            key={index}
                            {...section}
                            index={index}
                        />
                    ))}
                </div>

                {/* Bottom Decoration */}
                <div className="relative mt-16 border border-blue-500/30 rounded-lg bg-black/90 p-6">
                    <div className="absolute inset-px bg-gradient-to-b from-blue-500/10 to-transparent" />
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500/0 via-blue-500/50 to-blue-500/0" />

                    <div className="relative flex flex-col items-center text-center">
                        <div className="relative w-16 h-16 rounded-full border-2 border-blue-400/50 mb-4 overflow-hidden">
                            {/* Added image container with gradient overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-blue-500/20 to-transparent" />
                            <img
                                src={process.env.PUBLIC_URL + '/kyora.png'}
                                alt="Kyora"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <h3 className="text-2xl font-bold text-blue-200 mb-2">Join the Revolution</h3>
                        <p className="text-blue-200/70 max-w-2xl">
                            Experience the next evolution in token creation. Kyora is here to shape the future of crypto innovation.
                        </p>
                    </div>
                </div>
            </div>

            {/* Custom scrollbar styles */}
            <style jsx global>{`
        /* Webkit Scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        ::-webkit-scrollbar-track {
          background: #1e293b;
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb {
          background: #3b82f6;
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #60a5fa;
        }

        /* Firefox Scrollbar */
        * {
          scrollbar-width: thin;
          scrollbar-color: #3b82f6 #1e293b;
        }

        /* Hide scrollbar for Chrome, Safari and Opera */
        .overflow-x-auto::-webkit-scrollbar {
          height: 6px;
        }

        .overflow-x-auto::-webkit-scrollbar-track {
          background: rgba(30, 41, 59, 0.5);
          border-radius: 3px;
        }

        .overflow-x-auto::-webkit-scrollbar-thumb {
          background: rgba(59, 130, 246, 0.5);
          border-radius: 3px;
        }

        .overflow-x-auto::-webkit-scrollbar-thumb:hover {
          background: rgba(96, 165, 250, 0.7);
        }
      `}</style>
        </div>
    );
};

export default Roadmap;